import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';

/**
 * A value cell for the traffic table. The treatment follows what the value IS:
 *  - an address (IP / CIDR) or a port number renders as a monospace chip, color-coded source vs
 *    destination, with copy and filter actions;
 *  - an entity name (an identity, host, or service) renders as a link (it names an entity you can
 *    open) with the same copy and filter actions;
 *  - anything else (a grouped label, a blank) renders as plain monospace text.
 * The value is never reformatted - the chip, the link, the copy and the filter all carry it verbatim.
 * The actions appear only on hover or keyboard focus and are absolutely positioned over the value's
 * right edge, so it stays exactly as wide as its content and reserves no empty space (the same pattern
 * as the identities roles chips). `kind` decides the treatment; the page decides which filter each one
 * feeds. Shared by the source and destination address, port and identity columns across the flat and
 * tree views.
 */
@Component({
    selector: 'app-value-chip-cell',
    standalone: true,
    imports: [NgTemplateOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.vcc-truncate]': 'truncate',
    },
    template: `
        @if (text) {
            <span
                class="vcc-cell"
                [class.is-link]="isLink"
                [class.is-chip]="isChip"
                [class.is-filtered]="filtered"
                [attr.data-category]="isChip ? category : null"
            >
                @if (isLink) {
                    <button type="button" class="vcc-link" [attr.title]="text" (click)="open($event)">{{ text }}</button>
                } @else if (isChip) {
                    <span class="vcc-chip">
                        <span class="vcc-val">@if (prefix) {<span class="vcc-prefix">{{ prefix }}</span>}{{ text }}</span>
                    </span>
                } @else {
                    <span class="vcc-text">{{ text }}</span>
                }
                @if (isLink || isChip) {
                    <span class="vcc-actions">
                        <ng-container [ngTemplateOutlet]="valueActions" />
                    </span>
                }
            </span>
        }
        <ng-template #valueActions>
            <button
                type="button"
                class="vcc-btn vcc-copy"
                [class.is-copied]="copied"
                [attr.aria-label]="copied ? 'Copied' : 'Copy ' + text"
                [title]="copied ? 'Copied' : 'Copy'"
                (click)="copy($event)"
            >
                @if (copied) {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                } @else {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="11" height="11" rx="2" />
                        <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                    </svg>
                }
            </button>
            @if (onFilter) {
                <button
                    type="button"
                    class="vcc-btn vcc-filter"
                    [class.is-active]="filtered"
                    [attr.aria-pressed]="filtered"
                    [attr.aria-label]="(filtered ? 'Remove filter ' : 'Filter by ') + text"
                    [title]="filtered ? 'Remove filter' : 'Filter by this value'"
                    (click)="filter($event)"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 4h18l-7 8v6l-4 2v-8z" />
                    </svg>
                </button>
            }
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: flex;
                align-items: center;
                /* Fill the table cell - full width and full height - so the copy / filter actions reveal
                   when the pointer is anywhere in the cell, not only over the value or its center band.
                   align-self stretches us in a flex cell, min-height covers a block / table cell. The value
                   and its actions still sit left and vertically centered, content-width. */
                align-self: stretch;
                width: 100%;
                max-width: 100%;
                min-height: 100%;
            }
            .vcc-text {
                font-family: var(--font-mono);
                font-size: 0.8125rem;
                color: var(--text-primary);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            /* The cell wraps the value (chip / link / text) plus its inline actions, so copy and filter
               always sit just to the right of the value - outside a chip's pill, after a link's name - and
               never cover it. One --vcc-accent per cell drives the chip border and fill, the link underline,
               the action buttons, and the active-filter state: the value's role color for a chip, the
               standard interactive blue for a link. */
            .vcc-cell {
                display: inline-flex;
                align-items: center;
                max-width: 100%;
                min-width: 0;
                /* Match the identities roles chip height so the action buttons have vertical room and are
                   easy to hit (a short row crowds them against the top and bottom edges). */
                min-height: 1.75rem;
                --vcc-accent: var(--border-color-strong);
            }
            .vcc-cell.is-link {
                --vcc-accent: var(--primaryColor, #0273fb);
            }
            .vcc-cell[data-category='source'] {
                --vcc-accent: #0273fb;
            }
            .vcc-cell[data-category='destination'] {
                --vcc-accent: #0d9488;
            }
            /* Brighten each accent on the dark canvas so the color still carries. */
            :host-context(.darkmode) .vcc-cell.is-link,
            :host-context(.darkmode) .vcc-cell[data-category='source'] {
                --vcc-accent: #3b9bff;
            }
            :host-context(.darkmode) .vcc-cell[data-category='destination'] {
                --vcc-accent: #2dd4bf;
            }
            /* An address or port renders as a monospace chip, sized to its value so it never truncates
               (addresses and ports must read in full); the actions sit outside it, so it reserves no space
               for them. */
            .vcc-chip {
                display: inline-flex;
                align-items: center;
                flex: 0 0 auto;
                box-sizing: border-box;
                min-height: 1.75rem;
                font-family: var(--font-mono);
                font-size: 0.8125rem;
                line-height: 1;
                color: var(--text-primary);
                background: color-mix(in srgb, var(--vcc-accent) 9%, transparent);
                /* A soft tint of the chip's own accent, not the strong neutral border - this is a decorative
                   value chip, so the value text carries the contrast and the edge can stay quiet. */
                border: 0.0625rem solid color-mix(in srgb, var(--vcc-accent) 42%, transparent);
                border-radius: var(--radius-nested);
                padding: 0.15625rem 0.625rem;
            }
            /* A faint tint reads on a light row but nearly vanishes on a dark one, so lift the fill and the
               edge a touch in dark mode (still the accent hue, never a light neutral). */
            :host-context(.darkmode) .vcc-chip {
                background: color-mix(in srgb, var(--vcc-accent) 16%, transparent);
                border-color: color-mix(in srgb, var(--vcc-accent) 52%, transparent);
            }
            .vcc-cell.is-filtered .vcc-chip {
                border-color: var(--vcc-accent);
                background: color-mix(in srgb, var(--vcc-accent) 18%, transparent);
            }
            /* An entity name (identity / host / service) is a link, not a chip: it points at an entity you
               can open, so it reads as neutral text (no border or fill) that underlines in the interactive
               blue on hover. Rendered as a button (opening an in-page preview is an action, not a
               navigation) with the button and the global anchor styles both reset. It shrinks and clips so
               the inline actions beside it always have room. */
            .vcc-link {
                appearance: none;
                margin: 0;
                padding: 0;
                border: 0;
                background: none;
                flex: 0 1 auto;
                min-width: 0;
                max-width: 100%;
                text-align: left;
                font-family: var(--font-sans);
                font-size: 0.8125rem;
                font-weight: 600;
                color: var(--text-primary);
                text-decoration: none;
                text-decoration-color: var(--vcc-accent);
                text-underline-offset: 0.125rem;
                cursor: pointer;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            /* Underlines on hover, and while it is an active filter it stays a link (underlined, never a pill). */
            .vcc-link:hover,
            .vcc-cell.is-filtered .vcc-link {
                text-decoration: underline;
                text-decoration-color: var(--vcc-accent);
            }
            .vcc-val {
                white-space: nowrap;
            }
            /* A link can be long, so it truncates to the column (the full name stays in the title tooltip
               and the verbatim copy); let the host shrink so the ellipsis kicks in. Chips never truncate. */
            :host(.vcc-truncate) {
                min-width: 0;
            }
            /* A display-only lead character (e.g. ":" before a port) - muted so it reads as a separator,
               not part of the value. It is never copied or filtered on. */
            .vcc-prefix {
                color: var(--text-secondary);
            }
            /* The actions sit inline, just to the right of the value, and appear only on hover or keyboard
               focus; at rest the value shows alone. */
            .vcc-actions {
                display: inline-flex;
                align-items: center;
                flex: 0 0 auto;
                gap: 0.1875rem;
                padding-left: 0.375rem;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.12s ease;
            }
            :host(:hover) .vcc-actions,
            :host(:focus-within) .vcc-actions {
                opacity: 1;
                pointer-events: auto;
            }
            .vcc-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex: 0 0 auto;
                width: 1.25rem;
                height: 1.25rem;
                padding: 0;
                color: var(--vcc-accent);
                background: var(--lt-control-bg, var(--background, #fff));
                border: 0.0625rem solid color-mix(in srgb, var(--vcc-accent) 40%, transparent);
                border-radius: 0.3125rem;
                cursor: pointer;
                transition: background 0.12s, color 0.12s, border-color 0.12s;
            }
            .vcc-btn svg {
                width: 0.875rem;
                height: 0.875rem;
            }
            .vcc-btn:hover {
                color: var(--lt-control-bg, #fff);
                background: var(--vcc-accent);
                border-color: var(--vcc-accent);
            }
            .vcc-copy.is-copied,
            .vcc-copy.is-copied:hover {
                color: #16a34a;
                background: var(--lt-control-bg, var(--background, #fff));
                border-color: color-mix(in srgb, #16a34a 45%, transparent);
            }
            .vcc-filter.is-active,
            .vcc-filter.is-active:hover {
                color: var(--lt-control-bg, #fff);
                background: var(--vcc-accent);
                border-color: var(--vcc-accent);
            }
        `,
    ],
})
export class ValueChipCellComponent {
    text = '';
    isChip = false;
    isLink = false;
    copied = false;

    /** Addresses and ports render as chips; an entity name (identity / host / service) renders as a link;
        everything else is text. */
    @Input() set kind(k: 'address' | 'port' | 'identity' | 'host' | 'service') {
        this.valueKind = k;
        this.recomputeTreatment();
    }

    /** Entity names can be long, so a link truncates to the column; addresses and ports never do. */
    get truncate(): boolean {
        return this.isNameKind(this.valueKind);
    }
    /** Which accent the chip wears, so source / destination read apart at a glance (links are neutral). */
    @Input() category: 'source' | 'destination' = 'source';
    /** A display-only lead character shown before the value (e.g. ":" for a port). Not copied or filtered. */
    @Input() prefix = '';
    /** Whether this value is currently an active filter for its field (shows the funnel + chip as on). */
    @Input() filtered = false;
    /** Add or remove this value from its field's filter. Omit to render copy-only. */
    @Input() onFilter?: (value: string) => void;
    /** Open the entity this name points at (the side panel preview). Wired for later; links still render
        without it so the affordance is there before the panel exists. */
    @Input() onOpen?: (value: string) => void;

    private valueKind: 'address' | 'port' | 'identity' | 'host' | 'service' = 'address';
    private copyTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(private readonly cdr: ChangeDetectorRef) {}

    /** The value to show. '(empty)' and blanks render nothing. */
    @Input() set value(v: unknown) {
        const s = v == null ? '' : String(v).trim();
        this.text = s === '(empty)' ? '' : s;
        this.recomputeTreatment();
    }

    copy(event: Event): void {
        event.stopPropagation();
        const write = navigator.clipboard?.writeText(this.text);
        Promise.resolve(write)
            .then(() => this.flashCopied())
            .catch(() => {
                /* clipboard can be unavailable (permissions, insecure context) - leave the value visible to copy by hand */
            });
    }

    filter(event: Event): void {
        event.stopPropagation();
        this.onFilter?.(this.text);
    }

    open(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.onOpen?.(this.text);
    }

    /** An entity name is a link; an address or port is a chip; everything else is plain text. */
    private recomputeTreatment(): void {
        const nameKind = this.isNameKind(this.valueKind);
        this.isLink = nameKind && !!this.text;
        this.isChip = !nameKind && this.chipWorthy(this.text);
    }

    /** True for the kinds that name an entity you can open (rendered as a link, not a chip). */
    private isNameKind(k: string): boolean {
        return k === 'identity' || k === 'host' || k === 'service';
    }

    private flashCopied(): void {
        this.copied = true;
        this.cdr.markForCheck();
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
            this.copied = false;
            this.cdr.markForCheck();
        }, 1100);
    }

    /** True when an address or port value is chip-worthy (a real IP / CIDR, or a port in range). */
    private chipWorthy(s: string): boolean {
        return this.valueKind === 'port' ? ValueChipCellComponent.looksLikePort(s) : ValueChipCellComponent.looksLikeAddress(s);
    }

    /** True for a plain IPv4, IPv4 CIDR, or IPv6 (optionally with a prefix) - not hostnames or labels. */
    private static looksLikeAddress(s: string): boolean {
        if (!s) {
            return false;
        }
        const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
        const ipv6 = /^[0-9a-fA-F:]+(\/\d{1,3})?$/;
        return ipv4.test(s) || (s.includes(':') && ipv6.test(s));
    }

    /** True for a plain port number in 1-65535 - not ranges, names, or blanks. */
    private static looksLikePort(s: string): boolean {
        if (!/^\d{1,5}$/.test(s)) {
            return false;
        }
        const n = Number(s);
        return n >= 1 && n <= 65535;
    }
}
